import React from "react";
import { Box, Typography, Stack, Avatar, Chip, Divider, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BugReportIcon from "@mui/icons-material/BugReport";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { unwrapDescriptionString } from "../../utils/descriptionUtils";
import { parseTicketAnalysisMarkdown, flattenAdfContent } from "../../utils/ticketFormat";
import { formatShortWeekdayDateTime } from "../../../../utils/dateUtils";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatAssigneesDisplay(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string") {
    if (raw === "[object Object]" || !raw.trim()) return "";
    return raw;
  }
  if (Array.isArray(raw)) {
    return raw
      .map((a) => {
        if (a == null) return "";
        if (typeof a === "object")
          return a.displayName || a.name || a.emailAddress || a.email || a.accountId || "";
        return String(a);
      })
      .filter(Boolean)
      .join(", ");
  }
  if (typeof raw === "object")
    return raw.displayName || raw.name || raw.emailAddress || raw.email || raw.accountId || "";
  return String(raw);
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(/[\s,]+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function attachmentDisplayName(attachments, mediaId) {
  if (!mediaId || !Array.isArray(attachments)) return null;
  const idStr = String(mediaId);
  const hit = attachments.find((a) => a && String(a.id) === idStr);
  return hit?.name?.trim() || null;
}

// ─── Status / Type / Priority configs ────────────────────────────────────────

const STATUS_MAP = {
  "to do":       { color: "#44546F", bg: "#F1F2F4" },
  open:          { color: "#44546F", bg: "#F1F2F4" },
  "in progress": { color: "#0052CC", bg: "#DEEBFF" },
  "in review":   { color: "#403294", bg: "#EAE6FF" },
  done:          { color: "#006644", bg: "#E3FCEF" },
  closed:        { color: "#006644", bg: "#E3FCEF" },
  resolved:      { color: "#006644", bg: "#E3FCEF" },
  blocked:       { color: "#BF2600", bg: "#FFEBE6" },
};

const TYPE_MAP = {
  bug:     { color: "#BF2600", bg: "#FFEBE6", icon: BugReportIcon },
  story:   { color: "#006644", bg: "#E3FCEF", icon: AutoAwesomeIcon },
  task:    { color: "#0052CC", bg: "#DEEBFF", icon: TaskAltIcon },
  epic:    { color: "#403294", bg: "#EAE6FF", icon: AutoAwesomeIcon },
  subtask: { color: "#0052CC", bg: "#DEEBFF", icon: TaskAltIcon },
};

const PRIORITY_MAP = {
  highest: { color: "#BF2600", label: "Highest" },
  high:    { color: "#DE350B", label: "High" },
  medium:  { color: "#FF991F", label: "Medium" },
  low:     { color: "#0052CC", label: "Low" },
  lowest:  { color: "#00875A", label: "Lowest" },
};

function resolveStatus(status) {
  if (!status) return null;
  const key = String(status).toLowerCase().trim();
  return STATUS_MAP[key] || STATUS_MAP[Object.keys(STATUS_MAP).find((k) => key.includes(k)) || ""] || { color: "#44546F", bg: "#F1F2F4" };
}

function resolveType(type) {
  if (!type) return null;
  const key = String(type).toLowerCase().trim();
  return TYPE_MAP[key] || TYPE_MAP[Object.keys(TYPE_MAP).find((k) => key.includes(k)) || ""] || { color: "#44546F", bg: "#F1F2F4", icon: TaskAltIcon };
}

// ─── ADF Renderer ────────────────────────────────────────────────────────────

function AdfNodes({ nodes, tc, attachments, primaryMain }) {
  return nodes.map((node, i) => {
    if (node.type === "paragraph") {
      return (
        <Typography key={i} sx={{ fontSize: "0.855rem", lineHeight: 1.7, color: tc.text.primary, mb: 0.5 }}>
          {node.text}
        </Typography>
      );
    }
    if (node.type === "heading") {
      return (
        <Typography key={i} sx={{ fontSize: node.level <= 2 ? "1rem" : "0.92rem", fontWeight: 600, color: tc.text.strong, mb: 0.5, mt: 1 }}>
          {node.text}
        </Typography>
      );
    }
    if (node.type === "bulletList" || node.type === "orderedList") {
      const Tag = node.type === "orderedList" ? "ol" : "ul";
      return (
        <Box key={i} component={Tag} sx={{ m: "0.25rem 0 0.75rem 0", pl: "1.25rem", color: tc.text.primary, fontSize: "0.855rem", lineHeight: 1.7, "& li": { mb: "0.2rem" } }}>
          {node.items.map((item, j) => <Box component="li" key={j}>{item}</Box>)}
        </Box>
      );
    }
    if (node.type === "codeBlock") {
      return (
        <Box key={i} sx={{ fontFamily: "monospace", fontSize: "0.8rem", bgcolor: tc.background.tableRow, p: 1.5, borderRadius: "6px", whiteSpace: "pre-wrap", mb: 0.75, color: tc.text.primary, lineHeight: 1.6 }}>
          {node.text}
        </Box>
      );
    }
    if (node.type === "blockquote") {
      return (
        <Box key={i} sx={{ borderLeft: `3px solid ${primaryMain}`, pl: 1.5, my: 0.75, py: 0.5, color: tc.text.secondary }}>
          <AdfNodes nodes={node.children || []} tc={tc} attachments={attachments} primaryMain={primaryMain} />
        </Box>
      );
    }
    if (node.type === "mediaAttachment") {
      const syncedName = attachmentDisplayName(attachments, node.id);
      return (
        <Box key={i} sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mb: 0.5, py: 0.5, px: 1, borderRadius: "6px", bgcolor: tc.background.tableRow }}>
          <AttachFileIcon sx={{ fontSize: 14, color: tc.text.secondary, transform: "rotate(45deg)" }} />
          <Typography sx={{ fontSize: "0.78rem", color: tc.text.secondary, fontWeight: 500 }}>
            {syncedName || node.label}
          </Typography>
        </Box>
      );
    }
    if (node.type === "table") {
      return (
        <Box key={i} sx={{ overflowX: "auto", my: 1, borderRadius: "6px", border: `1px solid ${tc.border.subtle}` }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem", "& td, & th": { borderBottom: `1px solid ${tc.border.subtle}`, px: 1.5, py: 0.75, textAlign: "left", color: tc.text.primary }, "& tr:last-child td": { borderBottom: "none" }, "& tr:hover td": { bgcolor: alpha(primaryMain, 0.02) } }}>
            <tbody>
              {node.rows.map((row, ri) => (
                <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </Box>
        </Box>
      );
    }
    return null;
  });
}

// ─── Detail Row (Jira sidebar style) ─────────────────────────────────────────

function DetailRow({ icon: Icon, label, children, tc }) {
  if (!children) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 0.6, minHeight: 28 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 90, flexShrink: 0, pt: 0.15 }}>
        {Icon && <Icon sx={{ fontSize: 14, color: tc.text.disabled }} />}
        <Typography sx={{ fontSize: "0.72rem", color: tc.text.secondary, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function TicketEntityBody({ entityBodyText, contentObj, meta, tc }) {
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;
  const descStr = unwrapDescriptionString(entityBodyText);
  const trimmed = descStr.trim();
  const assigneesLabel = formatAssigneesDisplay(meta.assignees);
  const attachments = contentObj?.attachments || [];
  const priorityKey = String(meta.priority ?? "").toLowerCase().trim();
  const priorityCfg = PRIORITY_MAP[priorityKey];

  // ── Details panel (Jira right-sidebar style, rendered inline) ──────────
  const renderDetailsPanel = (ticketId, ticketType, folderList) => {
    const statusCfg = resolveStatus(meta.status);
    const typeCfg = resolveType(ticketType);
    const TypeIcon = typeCfg?.icon;
    const hasAnyDetail = meta.status || ticketType || ticketId || assigneesLabel || priorityCfg || meta.createdAt || folderList;
    if (!hasAnyDetail) return null;

    return (
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Stack spacing={0}>
          {meta.status && statusCfg && (
            <DetailRow icon={CheckCircleOutlineIcon} label="Status" tc={tc}>
              <Chip
                label={meta.status}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  bgcolor: statusCfg.bg,
                  color: statusCfg.color,
                  borderRadius: "4px",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </DetailRow>
          )}
          {ticketType && typeCfg && (
            <DetailRow icon={LabelOutlinedIcon} label="Type" tc={tc}>
              <Chip
                icon={TypeIcon ? <TypeIcon sx={{ fontSize: "13px !important", color: `${typeCfg.color} !important` }} /> : undefined}
                label={ticketType}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  bgcolor: typeCfg.bg,
                  color: typeCfg.color,
                  borderRadius: "4px",
                  "& .MuiChip-label": { px: 0.75 },
                  "& .MuiChip-icon": { ml: 0.5 },
                }}
              />
            </DetailRow>
          )}
          {priorityCfg && meta.priority && (
            <DetailRow icon={FlagIcon} label="Priority" tc={tc}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FlagIcon sx={{ fontSize: 14, color: priorityCfg.color }} />
                <Typography sx={{ fontSize: "0.8rem", color: tc.text.primary, fontWeight: 500 }}>
                  {priorityCfg.label}
                </Typography>
              </Box>
            </DetailRow>
          )}
          {assigneesLabel && (
            <DetailRow icon={PersonOutlineIcon} label="Assignee" tc={tc}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Avatar sx={{ width: 20, height: 20, fontSize: "0.58rem", fontWeight: 700, bgcolor: primaryMain, color: "#fff" }}>
                  {getInitials(assigneesLabel)}
                </Avatar>
                <Typography sx={{ fontSize: "0.8rem", color: tc.text.primary, fontWeight: 500 }}>
                  {assigneesLabel}
                </Typography>
              </Box>
            </DetailRow>
          )}
          {ticketId && (
            <DetailRow icon={LabelOutlinedIcon} label="ID" tc={tc}>
              <Typography sx={{ fontSize: "0.8rem", color: tc.text.primary, fontFamily: "monospace", fontWeight: 500 }}>
                {ticketId}
              </Typography>
            </DetailRow>
          )}
          {folderList && (
            <DetailRow icon={LabelOutlinedIcon} label="Project" tc={tc}>
              <Typography sx={{ fontSize: "0.8rem", color: tc.text.primary, fontWeight: 500 }}>
                {folderList}
              </Typography>
            </DetailRow>
          )}
          {meta.createdAt && (
            <DetailRow icon={AccessTimeIcon} label="Created" tc={tc}>
              <Typography sx={{ fontSize: "0.8rem", color: tc.text.primary }}>
                {formatShortWeekdayDateTime(meta.createdAt)}
              </Typography>
            </DetailRow>
          )}
        </Stack>
      </Box>
    );
  };

  // ── Branch 1: ADF JSON ────────────────────────────────────────────────────
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(descStr);
      if (parsed.type === "doc" && Array.isArray(parsed.content)) {
        const adfNodes = flattenAdfContent(parsed.content);
        return (
          <Box>
            {renderDetailsPanel("", "", "")}
            <Divider sx={{ borderColor: tc.border.subtle }} />
            <Box sx={{ px: 2.5, py: 2 }}>
              <AdfNodes nodes={adfNodes} tc={tc} attachments={attachments} primaryMain={primaryMain} />
            </Box>
          </Box>
        );
      }
    } catch { /* fall through */ }
  }

  // ── Branch 2: Ticket Analysis Markdown ───────────────────────────────────
  const ticketMd = parseTicketAnalysisMarkdown(descStr);
  if (ticketMd) {
    const tm = ticketMd.ticketMeta || {};
    const ticketTitle = tm.ticket_title || contentObj?.name || "";
    const ticketId = tm.ticket_id || "";
    const ticketType = tm.ticket_type || "";
    const folderList = tm["folder_/_list"] || "";

    return (
      <Box>
        {/* ── Title ── */}
        {ticketTitle && (
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: tc.text.strong, lineHeight: 1.4 }}>
              {ticketTitle}
            </Typography>
          </Box>
        )}

        {/* ── Details panel ── */}
        {renderDetailsPanel(ticketId, ticketType, folderList)}

        {/* ── Sections ── */}
        {ticketMd.sections.map((sec, idx) => {
          // ADF Description
          if (sec.type === "description_doc" && sec.doc) {
            const adfNodes = flattenAdfContent(sec.doc.content || []);
            return (
              <React.Fragment key={idx}>
                <Divider sx={{ borderColor: tc.border.subtle }} />
                <Box sx={{ px: 2.5, py: 1.75 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: tc.text.secondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                    Description
                  </Typography>
                  <AdfNodes nodes={adfNodes} tc={tc} attachments={attachments} primaryMain={primaryMain} />
                </Box>
              </React.Fragment>
            );
          }

          // Conversation / Status
          if (sec.type === "conversation") {
            const statusMatch = sec.body?.match(/Status:\s*(.+)/i);
            const statusVal = statusMatch?.[1]?.trim();
            const otherText = sec.body?.replace(/Status:\s*.+/i, "").trim();
            const hasContent = statusVal || otherText;
            if (!hasContent) return null;
            return (
              <React.Fragment key={idx}>
                <Divider sx={{ borderColor: tc.border.subtle }} />
                <Box sx={{ px: 2.5, py: 1.75 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: tc.text.secondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                    Activity
                  </Typography>
                  {statusVal && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: otherText ? 1 : 0 }}>
                      <Typography sx={{ fontSize: "0.78rem", color: tc.text.secondary }}>Workflow status:</Typography>
                      <Chip
                        label={statusVal}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.66rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          bgcolor: resolveStatus(statusVal)?.bg || tc.background.tableRow,
                          color: resolveStatus(statusVal)?.color || tc.text.secondary,
                          borderRadius: "4px",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </Box>
                  )}
                  {otherText && (
                    <Typography sx={{ fontSize: "0.855rem", lineHeight: 1.7, color: tc.text.primary, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {otherText}
                    </Typography>
                  )}
                </Box>
              </React.Fragment>
            );
          }

          const sectionTitle = (sec.title || "").trim().toLowerCase();
          const sectionBody = (sec.body || "").trim().toLowerCase();
          if (
            sec.type === "custom_fields" ||
            sectionTitle === "custom fields" ||
            sectionBody.startsWith("**customfield_") ||
            sectionBody.startsWith("customfield_")
          ) {
            return null;
          }

          // Generic text
          if (!sec.body?.trim()) return null;
          return (
            <React.Fragment key={idx}>
              <Divider sx={{ borderColor: tc.border.subtle }} />
              <Box sx={{ px: 2.5, py: 1.75 }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: tc.text.secondary, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
                  {sec.title}
                </Typography>
                <Typography sx={{ fontSize: "0.855rem", lineHeight: 1.7, color: tc.text.primary, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {sec.body}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    );
  }

  // ── Branch 3: Plain text fallback ──────────────────────────────────────────
  return (
    <Box>
      {renderDetailsPanel("", "", "")}
      {(meta.status || assigneesLabel || meta.createdAt) && <Divider sx={{ borderColor: tc.border.subtle }} />}
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography sx={{ fontSize: "0.855rem", lineHeight: 1.75, color: tc.text.primary, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {descStr}
        </Typography>
      </Box>
    </Box>
  );
}
